import { lazy, Suspense, useEffect, useState } from 'react'
import { Link, Route, Routes, useParams } from 'react-router-dom'
import { fetchCatalog, fetchCollection } from './api'
import { identity, identityMode, LoginLoopError } from './identity'
import type { Collection } from './types'

// hls.js is ~450 kB and only the player needs it. Splitting it out keeps the catalogue —
// the page a prospective buyer lands on — light, which is the page where bandwidth spent
// on JavaScript competes directly with nothing at all.
const PlayerRoute = lazy(() => import('./Player').then((m) => ({ default: m.PlayerRoute })))

export function App() {
  return (
    <Routes>
      {/* The callback must stay outside the gate: it is the route that *creates* the
          session, so requiring a session to reach it would deadlock the login. */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="*" element={<Gated />} />
    </Routes>
  )
}

/**
 * Nothing renders before there is a logged-in customer.
 *
 * An earlier version let the catalogue load anonymously and only asked for a login at the
 * point of pressing play. That is a defensible shape for a shop window, but it is not what
 * this product wants: the door is the login.
 */
function Gated() {
  const [state, setState] = useState<'checking' | 'ready' | 'blocked'>('checking')

  useEffect(() => {
    let alive = true
    ;(async () => {
      if (!(await identity.needsLogin())) {
        if (alive) setState('ready')
        return
      }
      try {
        await identity.login(window.location.pathname + window.location.search)
      } catch (err) {
        // A fresh login that lands straight back here is a loop, not a session problem.
        if (alive) setState(err instanceof LoginLoopError ? 'blocked' : 'blocked')
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  if (state === 'checking') return <div className="center">Verificando acesso…</div>

  if (state === 'blocked') {
    return (
      <div className="center error" style={{ flexDirection: 'column', gap: '1rem', padding: '2rem' }}>
        <p>Não foi possível iniciar sua sessão.</p>
        <p className="muted">
          O login foi concluído mas a sessão não se estabeleceu. Tente novamente; se persistir,
          fale com o suporte.
        </p>
        <button className="btn" onClick={() => void identity.logout()}>
          Sair e tentar de novo
        </button>
      </div>
    )
  }

  return (
    <>
      <Header />
      <Routes>
        <Route path="/" element={<CatalogRoute />} />
        <Route path="/c/:collection" element={<CollectionRoute />} />
        <Route
          path="/watch/:collection/:session"
          element={
            <Suspense fallback={<div className="center">Carregando player…</div>}>
              <PlayerRoute />
            </Suspense>
          }
        />
        <Route path="*" element={<div className="center">Página não encontrada.</div>} />
      </Routes>
    </>
  )
}

function Header() {
  const name = identity.displayName()
  const subjects = [
    'dev-entitled',
    'dev-session-only',
    'dev-all',
    'dev-locked',
    'dev-expired',
    'dev-revoked',
    'dev-soon',
  ]

  return (
    <header className="topbar">
      <Link to="/" className="brand">
        GCflix
      </Link>

      {/* Loud, permanent, and impossible to mistake for production. Mock identity proves
          nothing about who the viewer is. */}
      {identity.isMock && (
        <span className="mock-switch">
          <strong>SIMULADO</strong>
          {subjects.map((s) => (
            <a key={s} href={`?sub=${s}`} className={s === identity.mockSubject() ? 'active' : ''}>
              {s.replace('dev-', '')}
            </a>
          ))}
        </span>
      )}

      <span className="topbar-right">
        {name && <span className="muted">{name}</span>}
        <button className="linkbtn" onClick={() => void identity.logout()}>
          Sair
        </button>
      </span>
    </header>
  )
}

function CatalogRoute() {
  const [collections, setCollections] = useState<Collection[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchCatalog()
      .then(setCollections)
      .catch((e) => setError(String(e)))
  }, [])

  if (error) return <div className="center error">{error}</div>
  if (!collections) return <div className="center">Carregando…</div>

  return (
    <main className="page">
      <h1>Catálogo</h1>
      {collections.length === 0 && <p className="muted">Nenhuma coleção publicada ainda.</p>}
      <div className="grid">
        {collections.map((c) => (
          <Link key={c.key} to={`/c/${c.key}`} className="card">
            {c.coverUrl && <img src={c.coverUrl} alt="" />}
            <h2>{c.title}</h2>
            {c.subtitle && <p className="muted">{c.subtitle}</p>}
          </Link>
        ))}
      </div>
    </main>
  )
}

function CollectionRoute() {
  const { collection = '' } = useParams()
  const [data, setData] = useState<Collection | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchCollection(collection)
      .then(setData)
      .catch((e) => setError(String(e)))
  }, [collection])

  if (error) return <div className="center error">{error}</div>
  if (!data) return <div className="center">Carregando…</div>

  return (
    <main className="page">
      <Link to="/" className="back">
        ← Catálogo
      </Link>
      <h1>{data.title}</h1>
      {data.description && <p className="lead">{data.description}</p>}
      <ul className="sessions">
        {(data.sessions ?? []).map((s) => (
          <li key={s.key}>
            <Link to={`/watch/${data.key}/${s.key}`}>
              <span className="session-title">{s.title}</span>
              {s.durationSeconds > 0 && (
                <span className="muted">{Math.round(s.durationSeconds / 60)} min</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </main>
  )
}

function AuthCallback() {
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (identityMode !== 'oidc') {
      window.location.replace('/')
      return
    }
    identity
      .completeLoginRedirect()
      // Return to whatever they were trying to reach. Landing back on the catalogue after
      // a login they only started in order to open one lecture is a small betrayal.
      .then((returnTo) => window.location.replace(returnTo ?? '/'))
      .catch((e) => setError(String(e)))
  }, [])
  return <div className="center">{error ? `Falha no login: ${error}` : 'Entrando…'}</div>
}
